import React from 'react';
import { Gauge } from './DashboardCockpit';
import { Home, TrendingUp, DollarSign, AlertTriangle, GaugeCircle } from 'lucide-react';

/**
 * Owner portal cockpit — 4 animated gauges.
 * Props:
 *   cockpit: response.cockpit from /api/owner/dashboard
 */
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

const OwnerCockpit = ({ cockpit }) => {
  if (!cockpit) return null;
  const {
    occupied_properties = 0,
    total_properties = 0,
    occupancy_pct = 0,
    month_collected = 0,
    month_expected = 0,
    collection_pct = 0,
    anomalies_count = 0,
  } = cockpit;

  // Anomalies gauge is inverted: 0 anomalies is best. Cap at 10 for display.
  const anomaliesMax = 10;
  const anomaliesDisplay = Math.min(anomalies_count, anomaliesMax);

  return (
    <div
      className="rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-[#FDF6E3] via-white to-[#FDF6E3] p-4 md:p-6 mb-6 shadow-lg"
      data-testid="owner-cockpit"
    >
      <div className="flex items-center gap-2 mb-4">
        <GaugeCircle className="w-5 h-5 text-amber-700" />
        <h2 className="text-sm md:text-base font-bold text-[#1B3A4B] uppercase tracking-wider">
          Cockpit propriétaire
        </h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 1. Occupancy */}
        <div className="flex flex-col items-center" data-testid="gauge-occupancy">
          <Gauge
            value={occupancy_pct}
            max={100}
            size={170}
            thickness={13}
            label={`${occupancy_pct}%`}
            sub={`${occupied_properties}/${total_properties} unités`}
            color="#10B981"
            warningBelow={70}
            dangerBelow={40}
            startupDelay={100}
          />
          <div className="text-[10px] uppercase font-semibold text-gray-500 mt-1 flex items-center gap-1">
            <Home className="w-3 h-3" /> Taux d&apos;occupation
          </div>
        </div>
        {/* 2. Collection rate this month */}
        <div className="flex flex-col items-center" data-testid="gauge-collection">
          <Gauge
            value={collection_pct}
            max={100}
            size={170}
            thickness={13}
            label={`${collection_pct}%`}
            sub="encaissé ce mois"
            color="#D4A745"
            warningBelow={70}
            dangerBelow={40}
            startupDelay={250}
          />
          <div className="text-[10px] uppercase font-semibold text-gray-500 mt-1 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Encaissement mensuel
          </div>
        </div>
        {/* 3. Revenue collected vs expected */}
        <div className="flex flex-col items-center" data-testid="gauge-revenue">
          <Gauge
            value={month_collected}
            max={Math.max(month_expected, month_collected, 1)}
            size={170}
            thickness={13}
            label={`${fmt(month_collected)}`}
            sub={`/ ${fmt(month_expected)} FCFA`}
            color="#3B82F6"
            startupDelay={400}
          />
          <div className="text-[10px] uppercase font-semibold text-gray-500 mt-1 flex items-center gap-1">
            <DollarSign className="w-3 h-3" /> Revenus du mois
          </div>
        </div>
        {/* 4. Active anomalies */}
        <div className="flex flex-col items-center" data-testid="gauge-anomalies">
          <Gauge
            value={anomaliesDisplay}
            max={anomaliesMax}
            size={170}
            thickness={13}
            label={`${anomalies_count}`}
            sub="incident(s) actif(s)"
            color={anomalies_count === 0 ? '#10B981' : anomalies_count <= 2 ? '#F59E0B' : '#EF4444'}
            startupDelay={550}
          />
          <div className="text-[10px] uppercase font-semibold text-gray-500 mt-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Anomalies en cours
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerCockpit;
